/* eslint-disable object-curly-newline */
import { get } from 'lodash'
import PresentationApi from '@/api/presentation'
import sentry from '@/services/sentry'
import utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import EventBus from '@/eventBus'

const DEFAULT_SORT_COLUMN = 'deletedAt'
const DEFAULT_SORT_ORDER = 'desc'

const _state = {
  presentations: [],
  isLoading: false,
  isLoadingMore: false,
  isRestoring: false,
  sortColumn: DEFAULT_SORT_COLUMN,
  sortOrder: DEFAULT_SORT_ORDER,
  page: 1,
  totalPage: 1,
  selectedPresentation: null,
  isShowingPermanentlyDeleteModal: false,
}

const SET_TRASH_PRESENTATIONS = 'SET_TRASH_PRESENTATIONS'
const SET_LOADING_FLAG = 'SET_LOADING_FLAG'
const SET_RESTORING_FLAG = 'SET_RESTORING_FLAG'
const SET_CURRENT_PAGE = 'SET_CURRENT_PAGE'
const SET_TOTAL_PAGE = 'SET_TOTAL_PAGE'
const SET_LOADING_MORE = 'SET_LOADING_MORE'
const SET_SORT_COLUMN = 'SET_SORT_COLUMN'
const SET_SORT_ORDER = 'SET_SORT_ORDER'
const SET_SELECTED_PRESENTATION = 'SET_SELECTED_PRESENTATION'
const SET_IS_SHOWING_PERMANENTLY_DELETE_MODAL = 'SET_IS_SHOWING_PERMANENTLY_DELETE_MODAL'

const services = {
  getPresentationsFromTrash: async (queryParams) => {
    const response = await PresentationApi.getPresentationsFromTrash({
      deleted: true,
      ...queryParams,
    })
    return [get(response, 'result', []), get(response, 'numberOfPresentationPages', 1)]
  },
  restore: async (presentationIds) => {
    return PresentationApi.restoreFromTrash({ presentationIds })
  },
  permanentlyDelete: async (presentationIds) => {
    return PresentationApi.permanentlyDelete({ presentationIds })
  },
}

const _getters = {
  presentations: (state) => state.presentations,
  isLoading: (state) => state.isLoading,
  isRestoring: (state) => state.isRestoring,
  isLoadingMore: (state) => state.isLoadingMore,
  isLoadAll: (state) => { return state.page >= state.totalPage },
  queryParams: ({ page, sortColumn, sortOrder }) => ({ page, sortColumn, sortOrder }),
  selectedPresentation: (state) => state.selectedPresentation,
  isShowingPermanentlyDeleteModal: (state) => state.isShowingPermanentlyDeleteModal,
}

const actions = {
  async getDataAction({ dispatch, commit }) {
    commit(SET_LOADING_FLAG, true)
    try {
      await dispatch('getDeletedPresentationsAction')
    } finally {
      // Let the loading spinner show for at least 500ms
      setTimeout(() => {
        commit(SET_LOADING_FLAG, false)
      }, 500)
    }
  },
  async getDeletedPresentationsAction({ state, commit, getters }) {
    try {
      const [presentations, totalPage] = await services.getPresentationsFromTrash(getters.queryParams)

      commit(SET_TRASH_PRESENTATIONS, [...state.presentations, ...presentations])
      commit(SET_TOTAL_PAGE, totalPage)
    } catch (e) {
      sentry.captureException(e)
    }
  },
  async sortDeletedPresentationsAction({ dispatch, state, commit }, columnName) {
    commit(SET_LOADING_FLAG, true)
    try {
      const currentOrder = state.sortOrder
      const reverseOrder = currentOrder === 'desc' ? 'asc' : 'desc'
      const order = state.sortColumn === columnName ? reverseOrder : currentOrder
      commit(SET_SORT_COLUMN, columnName)
      commit(SET_SORT_ORDER, order)
      commit(SET_CURRENT_PAGE, 1)
      commit(SET_TRASH_PRESENTATIONS, [])
      await dispatch('getDeletedPresentationsAction')
    } finally {
      commit(SET_LOADING_FLAG, false)
    }
  },
  async getMoreDeletedPresentationAction({ dispatch, state, commit }) {
    commit(SET_LOADING_MORE, true)
    try {
      commit(SET_CURRENT_PAGE, state.page + 1)
      await dispatch('getDeletedPresentationsAction')
    } catch (e) {
      sentry.captureException(e)
    } finally {
      commit(SET_LOADING_MORE, false)
    }
  },
  async restorePresentationAction({ commit, getters }, id) {
    commit(SET_RESTORING_FLAG, true)
    try {
      const presentations = get(getters, 'presentations', [])
      const afterRestoreItemList = presentations.filter((prez) => prez.id !== id)
      await services.restore([id])
      commit(SET_TRASH_PRESENTATIONS, afterRestoreItemList)
    } catch (e) {
      sentry.captureException(e)
    } finally {
      commit(SET_RESTORING_FLAG, false)
    }
  },
  async resetTrashStateAction({ commit }) {
    commit(SET_TRASH_PRESENTATIONS, [])
    commit(SET_CURRENT_PAGE, 1)
    commit(SET_TOTAL_PAGE, 1)
    commit(SET_SORT_COLUMN, DEFAULT_SORT_COLUMN)
    commit(SET_SORT_ORDER, DEFAULT_SORT_ORDER)
  },
  async deletePresentationsAction({ dispatch }, ids) {
    try {
      await PresentationApi.deletePresentations(ids)

      if (this.deletedPresentations.length === 1) {
        utils.showToastSuccess(`${this.$i18nPresenter.t('Deleted presentation')} "${utils.sanitize(this.deletedPresentations[0].name)}"`)
      } else {
        utils.showToastSuccess(`${this.$i18nPresenter.t('Deleted')} ${this.deletedPresentations.length} ${this.$i18nPresenter.t('presentations')}`)
      }

      EventBus.$emit('reload-presentations-and-folders')

      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Presentation > Delete > Succeeded')
    } catch (error) {
      if (this.deletedPresentations.length === 1 && this.deletedPresentations[0]) {
        dispatch('setErrorOnInternalServerAction', `Cannot delete presentation ${utils.sanitize(this.deletedPresentations[0].name)}`, { root: true })
      } else {
        dispatch('setErrorOnInternalServerAction', `Cannot delete ${this.deletedPresentations.length} presentations`, { root: true })
      }

      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Presentation > Delete > Failed')
      throw error
    }
  },
  async selectPresentationAction({ commit }, presentation) {
    commit(SET_SELECTED_PRESENTATION, presentation)
  },
  async permanentlyDeletePresentationAction({ commit, getters }, id) {
    try {
      const presentations = get(getters, 'presentations', [])
      const afterDeleteItemList = presentations.filter((prez) => prez.id !== id)
      await services.permanentlyDelete([id])
      commit(SET_TRASH_PRESENTATIONS, afterDeleteItemList)
      commit(SET_IS_SHOWING_PERMANENTLY_DELETE_MODAL, false)
      return true
    } catch (e) {
      return false
    }
  },
  setIsShowingPermanentlyDeleteModalAction({ commit }, isShowing) {
    commit(SET_IS_SHOWING_PERMANENTLY_DELETE_MODAL, isShowing)
  },
  async emptyTrashAction({ commit }) {
    try {
      await services.permanentlyDelete(null)
      commit(SET_TRASH_PRESENTATIONS, [])
      commit(SET_IS_SHOWING_PERMANENTLY_DELETE_MODAL, false)
      return true
    } catch (e) {
      return false
    }
  },
}

const mutations = {
  [SET_TRASH_PRESENTATIONS](state, presentations) {
    state.presentations = presentations
  },
  [SET_LOADING_FLAG](state, isLoading) {
    state.isLoading = isLoading
  },
  [SET_RESTORING_FLAG](state, isRestoring) {
    state.isRestoring = isRestoring
  },
  [SET_CURRENT_PAGE](state, page) {
    state.page = page
  },
  [SET_TOTAL_PAGE](state, totalPage) {
    state.totalPage = totalPage
  },
  [SET_LOADING_MORE](state, loadingMore) {
    state.isLoadingMore = loadingMore
  },
  [SET_SORT_COLUMN](state, columnName) {
    state.sortColumn = columnName
  },
  [SET_SORT_ORDER](state, order) {
    state.sortOrder = order
  },
  [SET_SELECTED_PRESENTATION](state, presentation) {
    state.selectedPresentation = presentation
  },
  [SET_IS_SHOWING_PERMANENTLY_DELETE_MODAL](state, isShowing) {
    state.isShowingPermanentlyDeleteModal = isShowing
  },
}

const trash = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}

export default trash
